import classNames from "classnames";
import { ButtonVariant, CoButton } from "components/CoButton";
import { ScrollArea } from "components/ScrollArea/ScrollArea";
import { useSearchStepDrawer } from "features/Thread/components/SearchStepDrawer";
import { useThreadInteractionStepInfoBarEvents } from "features/Thread/components/ThreadInteractionStepInfoBar/hooks/useThreadInteractionStepInfoBarEvents";
import { useAgenticSearchStrategy } from "features/Thread/hooks/useAgenticSearchStrategy";
import {
  ProResearchGroup,
  ProResearchGroupType,
  ProResearchSummaryStatsDataModel,
} from "helpers/api";
import useLabels from "hooks/useLabels";
import { MinusIcon, PlusIcon } from "lucide-react";
import { Fragment } from "react";

const CITATION_GRAPH_SEARCH_GROUP_LABEL = "Explore citation graph";

/**
 * Props for the SearchStrategyDiagram component
 */
type SearchStrategyDiagramProps = {
  /** Optional thread interaction ID for fetching research data and managing drawer state */
  threadInteractionId?: string;
  /** Whether to show the search strategy diagram in the export mode */
  exportMode?: boolean;
};

/**
 * Represents a single step in the search strategy process
 */
type SearchStrategyStep = {
  /** The label/title for this step (e.g., "Identification", "Screening") */
  label: string;
  /** The total count of papers at this step */
  count: number;
  /** Array of content items describing what happened at this step */
  content: {
    /** Description text for this content item */
    label: string;
    /** Optional count of papers affected by this action */
    count?: number;
    /** Whether this action added (true) or removed (false) papers */
    added?: boolean;
  }[];
};

/**
 * Calculates search strategy statistics from research groups and summary data
 *
 * @param groups - Array of research groups containing search information
 * @param summaryStats - Summary statistics for the research process
 * @returns Object containing calculated statistics for each step
 */
const getSearchStrategyStats = (
  groups: ProResearchGroup[],
  summaryStats: ProResearchSummaryStatsDataModel
) => {
  const numSearches = groups.reduce(
    (acc, group) => acc + (group.searches?.length ?? 0),
    0
  );
  const numCitationGraphPapers = summaryStats?.num_citation_graph_papers ?? 0;
  const hasCitationGraphStep = groups.some(
    (group) =>
      (group.label &&
        group.label.includes(CITATION_GRAPH_SEARCH_GROUP_LABEL)) ||
      group.group_type === ProResearchGroupType.CITATION_GRAPH
  );

  // Base identified papers (without citation graph)
  const baseIdentifiedPapers =
    (summaryStats?.num_identified ?? 0) - numCitationGraphPapers;
  // Total identified papers (including citation graph)
  const totalIdentifiedPapers = summaryStats?.num_identified ?? 0;

  const screenedPapers = summaryStats?.num_screened ?? 0;
  const numDuplicates = totalIdentifiedPapers - screenedPapers;
  const eligiblePapers = summaryStats?.num_eligible ?? 0;
  const numNotRelevant = screenedPapers - eligiblePapers;
  const includedPapers = summaryStats?.num_included ?? 0;
  const numNotIncluded = eligiblePapers - includedPapers;

  return {
    numSearches,
    identifiedPapers: baseIdentifiedPapers,
    totalIdentifiedPapers,
    screenedPapers,
    numDuplicates,
    eligiblePapers,
    numNotRelevant,
    includedPapers,
    numNotIncluded,
    numCitationGraphPapers,
    hasCitationGraphStep,
  };
};

/**
 * A comprehensive diagram component that visualizes the systematic review
 * search strategy process, showing the flow from paper identification through
 * to final inclusion. Follows PRISMA (Preferred Reporting Items for Systematic
 * Reviews and Meta-Analyses) diagram conventions.
 *
 * Features:
 * - Four-step process: Identification → Screening → Eligibility → Included
 * - Shows paper counts at each stage with additions/removals
 * - Interactive details drawer for deep dive into search steps
 * - Responsive design with horizontal scrolling on mobile
 * - Integration with pro research data and analytics tracking
 * - Visual flow indicators with arrows between steps
 *
 * The diagram helps researchers understand:
 * - How many papers were found in initial searches
 * - How many duplicates were removed during screening
 * - How many papers were excluded for relevance during eligibility assessment
 * - Final number of papers included in the analysis
 *
 */
function SearchStrategyDiagram({
  threadInteractionId,
  exportMode = false,
}: SearchStrategyDiagramProps) {
  const [markdownLabels] = useLabels("search-strategy-diagram");

  // Get summary stats from useProResearch hook
  const { groups, summaryStats } = useAgenticSearchStrategy(
    threadInteractionId,
    true
  );

  const {
    numSearches,
    identifiedPapers,
    totalIdentifiedPapers,
    screenedPapers,
    numDuplicates,
    eligiblePapers,
    numNotRelevant,
    includedPapers,
    numNotIncluded,
    numCitationGraphPapers,
    hasCitationGraphStep,
  } = getSearchStrategyStats(groups, summaryStats ?? {});

  const identificationContent: {
    label: string;
    count?: number;
    added?: boolean;
  }[] = [
    {
      label: markdownLabels["descriptions"]["papers-identified"]
        .replace("{PAPER_COUNT}", identifiedPapers.toString())
        .replace("{SEARCH_COUNT}", numSearches.toString()),
      count: identifiedPapers,
      added: true,
    },
  ];

  // Add citation graph content if applicable
  if (hasCitationGraphStep && numCitationGraphPapers > 0) {
    identificationContent.push({
      label: (
        markdownLabels["descriptions"]["papers-from-citation-graph"] ||
        "Identified {COUNT} papers from citation graph exploration"
      ).replace("{COUNT}", numCitationGraphPapers.toString()),
      count: numCitationGraphPapers,
      added: true,
    });
  }

  const steps: SearchStrategyStep[] = [
    {
      label: markdownLabels["steps"]["identification"],
      count: totalIdentifiedPapers,
      content: identificationContent,
    },
    {
      label: markdownLabels["steps"]["screening"],
      count: screenedPapers,
      content: [
        {
          label: markdownLabels["descriptions"]["papers-removed"],
        },
        {
          label: markdownLabels["descriptions"]["papers-removed-duplicates"],
          count: numDuplicates,
          added: false,
        },
      ],
    },
    {
      label: markdownLabels["steps"]["eligibility"],
      count: eligiblePapers,
      content: [
        {
          label: markdownLabels["descriptions"]["papers-removed-not-relevant"],
          count: numNotRelevant,
          added: false,
        },
      ],
    },
    {
      label: markdownLabels["steps"]["included"],
      count: includedPapers,
      content: [
        {
          label: markdownLabels["descriptions"]["papers-included"].replace(
            "{INCLUDED_COUNT}",
            includedPapers.toString()
          ),
          count: numNotIncluded,
          added: false,
        },
      ],
    },
  ];

  const {
    interactionId: currentSearchStepDrawerInteractionId,
    openSearchStepDrawer,
    closeSearchStepDrawer,
  } = useSearchStepDrawer();
  const { trackDeepSearchStepDrawerToggle } =
    useThreadInteractionStepInfoBarEvents();

  /**
   * Handles opening/closing the detailed search step drawer
   */
  const handleClick = () => {
    if (threadInteractionId) {
      if (currentSearchStepDrawerInteractionId === threadInteractionId) {
        closeSearchStepDrawer();
        trackDeepSearchStepDrawerToggle(threadInteractionId, "Close");
      } else {
        openSearchStepDrawer(threadInteractionId, true); // true = manual open
        trackDeepSearchStepDrawerToggle(threadInteractionId, "Open");
      }
    }
  };

  // Wrapper component that conditionally applies ScrollArea based on export mode
  const ContentWrapper = ({ children }: { children: React.ReactNode }) => {
    return exportMode ? (
      <div>{children}</div>
    ) : (
      <ScrollArea orientation="horizontal">{children}</ScrollArea>
    );
  };

  return (
    <div className="flex flex-col gap-6 pb-6 mt-6 mb-3 w-full border-b">
      <div className="flex flex-1 items-center sm:justify-between">
        <h4 className="m-0 base-bold">Search Strategy</h4>
        {!exportMode && (
          <div className="hidden sm:block">
            <CoButton
              label={
                currentSearchStepDrawerInteractionId === threadInteractionId
                  ? markdownLabels["actions"]["hide-details"]
                  : markdownLabels["actions"]["all-details"]
              }
              variant={ButtonVariant.Tertiary}
              size="sm"
              iconRight={
                currentSearchStepDrawerInteractionId === threadInteractionId
                  ? undefined
                  : "icon-chevron-right"
              }
              onClick={handleClick}
            />
          </div>
        )}
      </div>
      <ContentWrapper>
        <div className="flex gap-3">
          {steps.map((step, index) => (
            <Fragment key={`step-${step.label}-${index}`}>
              <div
                className={classNames(
                  "flex flex-col gap-3 items-center w-full max-w-[300px]",
                  {
                    "min-w-[180px]": !exportMode,
                  }
                )}
              >
                <div className="flex flex-col w-full h-[4.5rem] items-center justify-center bg-bg-faint rounded-xl px-2 gap-1">
                  <span className="sm-md text-fg-base">{step.label}</span>
                  <span className="flex items-center gap-1.5 uppercase">
                    <span className="text-fg-muted tiny-mono-semibold">n</span>
                    <span className="text-fg-muted tiny-mono-semibold">=</span>
                    <span className="tiny-mono-bold">{step.count}</span>
                  </span>
                </div>
                {step.content.map((content, cIndex) => (
                  <Fragment key={`content-line-${cIndex}`}>
                    <div className="flex flex-col gap-1 h-8">
                      {Array.from({ length: 3 }).map((_, i) => (
                        <div
                          key={`content-line-${cIndex}-${i}`}
                          className="w-[2px] bg-bg-subtle flex-1 rounded-full"
                        />
                      ))}
                    </div>
                    <div
                      key={`content-${cIndex}`}
                      className="flex flex-col flex-1 gap-1 items-center"
                    >
                      <span className="text-center sm-md text-fg-base">
                        {content.label}
                      </span>

                      {content.count !== undefined && (
                        <span className="flex items-center gap-1.5">
                          {content.added ? (
                            <PlusIcon className="w-4 h-4 text-chart-green-base pb-px" />
                          ) : (
                            <MinusIcon className="w-4 h-4 text-chart-red-base" />
                          )}
                          <span className="tiny-mono-bold">
                            {content.count}
                          </span>
                          <span className="text-fg-muted tiny-normal">
                            {content.added
                              ? markdownLabels["modifications"]["identified"]
                              : markdownLabels["modifications"]["removed"]}
                          </span>
                        </span>
                      )}
                    </div>
                  </Fragment>
                ))}
              </div>
              {index < steps.length - 1 && (
                <span
                  key={`step-arrow-${step.label}-${index}`}
                  className="h-[4.5rem] flex items-center justify-center lg-mono-bold text-fg-muted"
                >
                  →
                </span>
              )}
            </Fragment>
          ))}
        </div>
      </ContentWrapper>
      {!exportMode && (
        <CoButton
          label={
            currentSearchStepDrawerInteractionId === threadInteractionId
              ? markdownLabels["actions"]["hide-details"]
              : markdownLabels["actions"]["all-details"]
          }
          variant={ButtonVariant.Tertiary}
          size="sm"
          iconRight={
            currentSearchStepDrawerInteractionId === threadInteractionId
              ? undefined
              : "icon-chevron-right"
          }
          onClick={handleClick}
          className="flex w-full sm:hidden"
        />
      )}
    </div>
  );
}

export const SearchStrategyDiagramExport = ({
  threadInteractionId,
}: SearchStrategyDiagramProps) => {
  return (
    <div className="break-inside-avoid">
      <SearchStrategyDiagram
        threadInteractionId={threadInteractionId}
        exportMode={true}
      />
    </div>
  );
};

/**
 * Converts search strategy diagram data to markdown table format for plain text export
 *
 * @param props - SearchStrategyDiagram props containing thread interaction ID
 * @returns Formatted markdown table showing the four-step process with counts
 */
export const SearchStrategyDiagramMarkdown = ({
  threadInteractionId,
}: SearchStrategyDiagramProps) => {
  const { groups, summaryStats } = useAgenticSearchStrategy(
    threadInteractionId,
    true
  );
  const [markdownLabels] = useLabels("search-strategy-diagram");

  const {
    totalIdentifiedPapers,
    screenedPapers,
    eligiblePapers,
    includedPapers,
  } = getSearchStrategyStats(groups, summaryStats ?? {});

  return `| ${markdownLabels.steps.identification} | ${markdownLabels.steps.screening} | ${markdownLabels.steps.eligibility} | ${markdownLabels.steps.included} |
|------------|----------|----------|----------|
| ${totalIdentifiedPapers} | ${screenedPapers} | ${eligiblePapers} | ${includedPapers} | \n`;
};

export default SearchStrategyDiagram;
